import { useEffect, useMemo, useState } from 'react'
import {
  Box, Chip, Stack, Tooltip, Typography, alpha,
} from '@mui/material'
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded'
import AppleIcon from '@mui/icons-material/Apple'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import { AnimatePresence, motion } from 'framer-motion'

const ANDROID_FULL = import.meta.env.VITE_ANDROID_APK_URL ?? ''
const ANDROID_LITE = import.meta.env.VITE_ANDROID_LITE_APK_URL ?? ''
const VERSION_FULL = import.meta.env.VITE_ANDROID_APP_VERSION ?? 'Latest'
const VERSION_LITE = import.meta.env.VITE_ANDROID_LITE_APP_VERSION ?? 'Latest'

// Cadence of the auto-advancing carousel inside each phone frame. 4.5s
// gives a reader long enough to absorb a screen before it changes, but
// keeps the page feeling alive.
const ROTATE_MS = 4500

// ─── Tab ────────────────────────────────────────────────────────────────────

export function DownloadsTab() {
  return (
    <Stack spacing={5} sx={{ pb: 6, maxWidth: 1320, mx: 'auto' }}>
      <HeroSection />
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4}>
        <AppShowcase
          variant="lite"
          title="Lite app"
          subtitle="For technicians on the floor"
          description="Stripped-down, fast, offline-first. Designed for one job at a time: open the work order, scan a QR, run the checklist, close out."
          features={[
            { icon: <BoltOutlinedIcon sx={{ fontSize: 14 }} />, label: 'Offline-first sync' },
            { icon: <QrCode2OutlinedIcon sx={{ fontSize: 14 }} />, label: 'Scan asset / location QRs' },
            { icon: <TranslateRoundedIcon sx={{ fontSize: 14 }} />, label: '11 Indian languages' },
            { icon: <EventAvailableRoundedIcon sx={{ fontSize: 14 }} />, label: 'Upcoming work at a glance' },
          ]}
          androidUrl={ANDROID_LITE}
          version={VERSION_LITE}
          screens={[
            { label: 'Home',       render: () => <LiteHomeScreen /> },
            { label: 'Work order', render: () => <LiteWorkOrderScreen /> },
            { label: 'Checklist',  render: () => <LiteChecklistScreen /> },
          ]}
        />
        <AppShowcase
          variant="full"
          title="Full app"
          subtitle="For supervisors & ops managers"
          description="The complete CMMS in your pocket — manage assets, vendors, parts, teams, work orders, checklists, and reports without opening a laptop."
          features={[
            { icon: <HandymanOutlinedIcon sx={{ fontSize: 14 }} />, label: 'Full WO management' },
            { icon: <GroupsRoundedIcon sx={{ fontSize: 14 }} />, label: 'Team + vendor coordination' },
            { icon: <BarChartRoundedIcon sx={{ fontSize: 14 }} />, label: 'Live dashboards' },
            { icon: <WifiOffRoundedIcon sx={{ fontSize: 14 }} />, label: 'Works offline + syncs back' },
          ]}
          androidUrl={ANDROID_FULL}
          version={VERSION_FULL}
          screens={[
            { label: 'Dashboard',  render: () => <FullDashboardScreen /> },
            { label: 'Work order', render: () => <FullWorkOrderScreen /> },
            { label: 'Asset',      render: () => <FullAssetScreen /> },
          ]}
        />
      </Stack>
      <InstallNotes />
    </Stack>
  )
}

// ─── Hero ────────────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden',
      p: { xs: 4, md: 5.5 }, borderRadius: 4,
      bgcolor: (t) => t.palette.mode === 'light'
        ? 'rgba(13,148,136,0.06)' : 'rgba(13,148,136,0.10)',
      border: '1px solid', borderColor: (t) => alpha(t.palette.primary.main, 0.25),
    }}>
      <Box sx={{
        position: 'absolute', right: -120, top: -120,
        width: 360, height: 360, borderRadius: '50%',
        background: (t) => `radial-gradient(circle, ${alpha(t.palette.primary.main, 0.18)} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center"
        sx={{ position: 'relative' }}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip icon={<AndroidRoundedIcon sx={{ fontSize: 14 }} />}
              label="Android available now" size="small"
              sx={{
                height: 24, fontWeight: 700, fontSize: 11,
                bgcolor: '#34A853', color: 'white',
                '& .MuiChip-icon': { color: 'white' },
              }} />
            <Chip icon={<AppleIcon sx={{ fontSize: 14 }} />}
              label="iOS coming soon" size="small"
              sx={{
                height: 24, fontWeight: 700, fontSize: 11,
                bgcolor: 'action.hover', color: 'text.secondary',
              }} />
          </Stack>
          <Typography sx={{
            fontSize: { xs: 26, md: 34 }, fontWeight: 800,
            letterSpacing: '-0.025em', lineHeight: 1.15, mb: 1.5,
          }}>
            Take CMMS to the field.
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: 'text.secondary', maxWidth: 560, lineHeight: 1.6 }}>
            Two purpose-built mobile apps — one stripped-down for technicians,
            one full-featured for supervisors. Both work offline, both sync the
            moment you reconnect. Pick the one that fits your role below.
          </Typography>
        </Box>
        <Box sx={{
          width: 96, height: 96, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider',
          boxShadow: (t) => `0 12px 28px -8px ${alpha(t.palette.primary.main, 0.35)}`,
        }}>
          <RocketLaunchOutlinedIcon sx={{ fontSize: 44, color: 'primary.main' }} />
        </Box>
      </Stack>
    </Box>
  )
}

// ─── App showcase card ──────────────────────────────────────────────────────

interface Screen { label: string; render: () => React.ReactNode }
interface ShowcaseProps {
  variant: 'lite' | 'full'
  title: string
  subtitle: string
  description: string
  features: { icon: React.ReactNode; label: string }[]
  androidUrl: string
  version: string
  screens: Screen[]
}

function AppShowcase({
  variant, title, subtitle, description, features, androidUrl, version, screens,
}: ShowcaseProps) {
  const accent = variant === 'lite' ? '#0D9488' : '#7C3AED'
  const accentBg = variant === 'lite' ? '#0D948818' : '#7C3AED18'

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
      style={{ flex: 1, minWidth: 0 }}
    >
      <Box sx={{
        p: { xs: 3, md: 4 }, borderRadius: 4,
        border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute', top: 0, left: 0, right: 0, height: 4,
          background: `linear-gradient(90deg, ${accent}, ${accent}AA)`,
        },
      }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 4, sm: 5 }}
          alignItems="center">
          <Box sx={{ flexShrink: 0 }}>
            <InteractivePhone accent={accent} screens={screens} />
          </Box>

          <Stack spacing={2.5} sx={{
            flex: 1, minWidth: 0,
            alignItems: { xs: 'center', sm: 'flex-start' },
            textAlign: { xs: 'center', sm: 'left' },
          }}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: accent, mb: 0.5 }}>
                {subtitle}
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em',
                lineHeight: 1.15 }}>
                {title}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6 }}>
              {description}
            </Typography>

            <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}
              justifyContent={{ xs: 'center', sm: 'flex-start' }}>
              {features.map((f, i) => (
                <Chip key={i}
                  icon={<Box sx={{ display: 'flex', color: accent }}>{f.icon}</Box>}
                  label={f.label}
                  size="small"
                  sx={{
                    height: 26, fontWeight: 600, fontSize: 11.5,
                    bgcolor: accentBg, color: 'text.primary',
                    '& .MuiChip-icon': { ml: '6px !important' },
                  }} />
              ))}
            </Stack>

            <Stack direction="row" useFlexGap flexWrap="wrap"
              sx={{ pt: 1, gap: 1.25, width: '100%' }}
              justifyContent={{ xs: 'center', sm: 'flex-start' }}>
              <AndroidDownloadButton url={androidUrl} version={version} accent={accent} />
              <IosComingSoonButton />
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </motion.div>
  )
}

// ─── Download buttons ───────────────────────────────────────────────────────

// ─── Store-badge style buttons ──────────────────────────────────────────────
//
// Modelled on the official Google Play / App Store badges: chunky icon on
// the left, two-line "Download for / Android" label on the right, single
// rectangle shape (not a pill — pills wrap the label awkwardly). Fixed
// min-width so the two badges line up; whiteSpace nowrap so the label
// never breaks across lines no matter how cramped the parent gets.

interface StoreBadgeProps {
  enabled: boolean
  href?: string
  tooltip: string
  bg: string
  fg: string
  icon: React.ReactNode
  smallLine: string
  bigLine: string
  hoverable?: boolean
}

function StoreBadge({
  enabled, href, tooltip, bg, fg, icon, smallLine, bigLine, hoverable,
}: StoreBadgeProps) {
  return (
    <Tooltip title={tooltip} arrow>
      <Box
        component={enabled ? 'a' : 'div'}
        href={enabled ? href : undefined}
        download={enabled ? '' : undefined}
        sx={{
          display: 'inline-flex', alignItems: 'center',
          gap: 1,
          // No minWidth — the content (icon + nowrap labels) already
          // sets a natural floor. Locking a minWidth caused the two
          // badges to overflow narrower right-columns and get clipped
          // by the card's overflow:hidden.
          px: 1.75, py: 1.1,
          borderRadius: 2,
          bgcolor: bg,
          color: fg,
          textDecoration: 'none',
          cursor: enabled ? 'pointer' : 'default',
          opacity: enabled ? 1 : 0.75,
          border: hoverable ? 'none' : '1.5px solid',
          borderColor: hoverable ? 'transparent' : 'divider',
          boxShadow: hoverable ? `0 10px 24px -10px ${bg}` : 'none',
          transition: 'transform 220ms ease, box-shadow 220ms ease, filter 220ms ease',
          '&:hover': hoverable ? {
            transform: 'translateY(-1px)',
            filter: 'brightness(1.06)',
            boxShadow: `0 14px 28px -10px ${bg}`,
          } : {},
        }}
      >
        <Box sx={{
          width: 28, height: 28, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Stack sx={{ minWidth: 0 }}>
          <Box sx={{
            fontSize: 9.5, fontWeight: 700, opacity: 0.85,
            lineHeight: 1, letterSpacing: 0.3, whiteSpace: 'nowrap',
          }}>
            {smallLine}
          </Box>
          <Box sx={{
            fontSize: 13.5, fontWeight: 800,
            letterSpacing: '-0.01em', lineHeight: 1.15, mt: 0.4,
            whiteSpace: 'nowrap',
          }}>
            {bigLine}
          </Box>
        </Stack>
      </Box>
    </Tooltip>
  )
}

function AndroidDownloadButton({ url, version, accent }: { url: string; version: string; accent: string }) {
  const enabled = url.trim().length > 0
  return (
    <StoreBadge
      enabled={enabled}
      href={url}
      tooltip={enabled
        ? 'Direct APK download — install requires "Unknown sources" enabled'
        : 'No APK URL configured. Set VITE_ANDROID_APK_URL (or VITE_ANDROID_LITE_APK_URL) in your env.'
      }
      bg={enabled ? accent : '#94A3B8'}
      fg="white"
      icon={<AndroidRoundedIcon sx={{ fontSize: 26 }} />}
      smallLine="DOWNLOAD FOR"
      bigLine={`Android  ·  ${version}`}
      hoverable={enabled}
    />
  )
}

function IosComingSoonButton() {
  return (
    <StoreBadge
      enabled={false}
      tooltip="Pending App Store review — we'll flip this on the day it lands."
      bg="transparent"
      fg="#475569"
      icon={<AppleIcon sx={{ fontSize: 28, color: '#0F172A' }} />}
      smallLine="COMING SOON TO"
      bigLine="iOS  ·  App Store"
    />
  )
}

// ─── Interactive phone frame ────────────────────────────────────────────────
//
// Pure-CSS iPhone body that hosts an auto-rotating carousel of in-app
// screens. The carousel:
//   • advances every `ROTATE_MS` ms
//   • can be jumped to a specific screen by clicking a dot at the bottom
//   • pauses when the user hovers the frame (so they can read a screen
//     without it changing under them)
//   • cross-fades between screens via framer-motion so the transition
//     feels like a real app, not a static slideshow

function InteractivePhone({ accent, screens }: { accent: string; screens: Screen[] }) {
  const [idx, setIdx] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused || screens.length <= 1) return
    const t = setInterval(() => setIdx((i) => (i + 1) % screens.length), ROTATE_MS)
    return () => clearInterval(t)
  }, [paused, screens.length])

  const current = useMemo(() => screens[idx] ?? screens[0]!, [idx, screens])

  return (
    <Stack alignItems="center" spacing={1.5}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}>
      <Box
        sx={{
          width: 296, height: 608, position: 'relative',
          transition: 'transform 350ms cubic-bezier(0.32, 0.72, 0, 1)',
          transform: paused ? 'rotateX(2deg) rotateY(-4deg) scale(1.015)' : 'none',
          transformStyle: 'preserve-3d',
          perspective: 1400,
        }}
      >
        {/* Device chassis */}
        <Box sx={{
          position: 'absolute', inset: 0,
          borderRadius: '46px',
          bgcolor: '#0F172A',
          boxShadow: (t) => `
            0 36px 70px -22px ${alpha(accent, 0.45)},
            0 22px 42px -14px rgba(15, 23, 42, 0.45),
            inset 0 0 0 2px ${alpha(t.palette.divider, 0.6)}
          `,
          p: 1.25,
        }}>
          {/* Screen */}
          <Box sx={{
            width: '100%', height: '100%',
            borderRadius: '38px',
            bgcolor: '#FFFFFF',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Dynamic-island-style notch */}
            <Box sx={{
              position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)',
              width: 96, height: 22, borderRadius: '99px',
              bgcolor: '#0F172A', zIndex: 3,
            }} />
            {/* Status bar */}
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 38,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2.5, fontSize: 11, color: '#0F172A', fontWeight: 700,
              fontFamily: 'ui-monospace, monospace', zIndex: 2, pt: 1.2,
            }}>
              <Box>9:41</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.85 }}>
                <Box sx={{ width: 14, height: 8, borderRadius: 0.4, border: '1.4px solid #0F172A' }} />
              </Box>
            </Box>
            {/* App content — animated swap */}
            <Box sx={{ pt: '42px', height: '100%', position: 'relative' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                  style={{ height: '100%' }}
                >
                  {current.render()}
                </motion.div>
              </AnimatePresence>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Carousel dot navigation + screen label */}
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: 0.5 }}>
        {screens.map((s, i) => {
          const active = i === idx
          return (
            <Box key={i}
              onClick={() => setIdx(i)}
              role="button"
              aria-label={`Show ${s.label} screen`}
              sx={{
                cursor: 'pointer',
                px: 1.25, py: 0.5, borderRadius: 99,
                bgcolor: active ? accent : 'transparent',
                color: active ? 'white' : 'text.secondary',
                border: '1px solid',
                borderColor: active ? accent : 'divider',
                fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                textTransform: 'uppercase',
                transition: 'all 220ms ease',
                '&:hover': { borderColor: accent, color: active ? 'white' : accent },
              }}
            >
              {s.label}
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// LITE APP SCREENS
// ════════════════════════════════════════════════════════════════════════════

// ─── Lite — Home ────────────────────────────────────────────────────────────

function LiteHomeScreen() {
  return (
    <Box sx={{ px: 1.75, pb: 1.5, height: '100%', overflow: 'hidden',
      bgcolor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Greeting */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#0D9488',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 13, fontWeight: 800,
          boxShadow: '0 4px 10px -3px rgba(13,148,136,0.5)' }}>JS</Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: 0.5 }}>
            GOOD MORNING
          </Box>
          <Box sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A', mt: 0.1 }}>
            John Smith
          </Box>
        </Box>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'white',
          border: '1px solid #E2E8F0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15 }}>🔔</Box>
      </Stack>

      {/* "Your work" — amber pinned banner */}
      <Box sx={{
        bgcolor: '#FFFBEB', border: '1px solid #FCD34D',
        borderRadius: 2, p: 1.5, mb: 1.75,
      }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: '#FEF3C7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📋</Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontSize: 12.5, fontWeight: 800, color: '#7C2D12' }}>Your work</Box>
            <Box sx={{ fontSize: 10, fontWeight: 500, color: '#92400E', mt: 0.15 }}>
              2 in progress · 3 open
            </Box>
          </Box>
          <Box sx={{ px: 1, py: 0.4, borderRadius: 1, bgcolor: 'white',
            border: '1px solid #FCD34D', fontSize: 11, fontWeight: 800, color: '#B45309' }}>
            5
          </Box>
        </Stack>
        <MiniWoRow status="IN PROGRESS" statusBg="#CCFBF1" statusFg="#0F766E"
          title="Replace lobby AC filter" date={['JUN', '8']} location="Floor 3 · HVAC" />
        <Box sx={{ height: 6 }} />
        <MiniWoRow status="OPEN" statusBg="#DBEAFE" statusFg="#1D4ED8"
          title="Inspect rooftop unit B" date={['JUN', '9']} location="Roof · Unit B" />
      </Box>

      {/* Metric tiles — 2 bigger ones */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <BigMetric label="Due today" value="2" color="#F97316" tone="#FFF7ED" />
        <BigMetric label="Done · 7d" value="11" color="#10B981" tone="#ECFDF5" />
      </Stack>

      {/* Due today list teaser */}
      <Box sx={{ px: 0.25, fontSize: 11, fontWeight: 800, letterSpacing: 1,
        color: '#64748B', mb: 0.75 }}>DUE TODAY</Box>
      <Box sx={{
        bgcolor: 'white', borderRadius: 1.5, p: 1.25,
        border: '1px solid #E2E8F0', mb: 0.75,
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#F97316' }} />
          <Box sx={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
            Replace lobby AC filter
          </Box>
          <Box sx={{ fontSize: 10.5, color: '#64748B', fontWeight: 600 }}>09:00</Box>
        </Stack>
      </Box>
    </Box>
  )
}

function MiniWoRow({ status, statusBg, statusFg, title, date, location }: {
  status: string; statusBg: string; statusFg: string;
  title: string; date: [string, string]; location: string
}) {
  const [month, day] = date
  return (
    <Box sx={{
      bgcolor: 'white', borderRadius: 1.5, p: 1,
      border: '1px solid #FDE68A',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          width: 36, height: 40, borderRadius: 1, bgcolor: '#FEF3C7',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Box sx={{ fontSize: 9, fontWeight: 800, color: '#B45309', letterSpacing: 0.5 }}>{month}</Box>
          <Box sx={{ fontSize: 15, fontWeight: 800, color: '#7C2D12', lineHeight: 1 }}>{day}</Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{
            fontSize: 9, fontWeight: 800, mb: 0.4,
            px: 0.6, py: 0.15, borderRadius: 0.5,
            bgcolor: statusBg, color: statusFg, letterSpacing: 0.5,
            display: 'inline-block',
          }}>{status}</Box>
          <Box sx={{ fontSize: 12, fontWeight: 700, color: '#0F172A',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </Box>
          <Box sx={{ fontSize: 10, color: '#64748B', mt: 0.15 }}>{location}</Box>
        </Box>
      </Stack>
    </Box>
  )
}

function BigMetric({ label, value, color, tone }: { label: string; value: string; color: string; tone: string }) {
  return (
    <Box sx={{
      flex: 1, bgcolor: 'white', borderRadius: 2, p: 1.25,
      border: '1px solid #E2E8F0',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.25, bgcolor: tone,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 17, fontWeight: 800, color,
        }}>{value}</Box>
        <Box>
          <Box sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase' }}>{label}</Box>
        </Box>
      </Stack>
    </Box>
  )
}

// ─── Lite — Work order detail ───────────────────────────────────────────────

function LiteWorkOrderScreen() {
  return (
    <Box sx={{ height: '100%', overflow: 'hidden',
      bgcolor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <AppBar number="WO-00214" title="Work order" />
      <Box sx={{ p: 1.75 }}>
        <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
          <StatusChip text="IN PROGRESS" bg="#CCFBF1" fg="#0F766E" />
          <StatusChip text="HIGH"        bg="#FEE2E2" fg="#DC2626" />
        </Stack>

        <Box sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A',
          lineHeight: 1.3, mb: 1 }}>
          Replace lobby AC filter
        </Box>
        <Box sx={{ fontSize: 11, color: '#64748B', mb: 1.75, lineHeight: 1.55 }}>
          Quarterly filter change for the lobby unit. Pull the access
          panel, swap the MERV-13 filter, log model + date.
        </Box>

        {/* Location card */}
        <Box sx={{
          bgcolor: 'white', borderRadius: 2, p: 1.25,
          border: '1px solid #E2E8F0', mb: 1.25,
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 30, height: 30, borderRadius: 1, bgcolor: '#0D948818',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#0D9488', fontSize: 16 }}>📍</Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontSize: 9.5, fontWeight: 800, color: '#94A3B8', letterSpacing: 0.4 }}>
                LOCATION
              </Box>
              <Box sx={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
                Floor 3 · HVAC closet
              </Box>
            </Box>
            <Box sx={{ fontSize: 11, color: '#0D9488', fontWeight: 800 }}>QR ›</Box>
          </Stack>
        </Box>

        {/* Checklist progress */}
        <Box sx={{
          bgcolor: 'white', borderRadius: 2, p: 1.25,
          border: '1px solid #E2E8F0', mb: 1.25,
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Box sx={{ fontSize: 11.5, fontWeight: 800, color: '#0F172A' }}>
              Filter swap checklist
            </Box>
            <Box sx={{ fontSize: 10.5, fontWeight: 700, color: '#0D9488' }}>3 of 5</Box>
          </Stack>
          <Box sx={{ height: 6, borderRadius: 99, bgcolor: '#E2E8F0', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: '60%', bgcolor: '#0D9488',
              borderRadius: 99 }} />
          </Box>
        </Box>

        {/* Primary CTA */}
        <Box sx={{
          bgcolor: '#0D9488', color: 'white', borderRadius: 99,
          py: 1.25, textAlign: 'center', fontSize: 12, fontWeight: 800,
          boxShadow: '0 8px 18px -6px rgba(13, 148, 136, 0.55)',
          letterSpacing: 0.3,
        }}>
          CONTINUE CHECKLIST
        </Box>
      </Box>
    </Box>
  )
}

// ─── Lite — Checklist ───────────────────────────────────────────────────────

function LiteChecklistScreen() {
  return (
    <Box sx={{ height: '100%', overflow: 'hidden',
      bgcolor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <AppBar number="WO-00214" title="Filter swap" />
      <Box sx={{ p: 1.75 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Box>
            <Box sx={{ fontSize: 10, fontWeight: 800, color: '#94A3B8', letterSpacing: 0.5 }}>
              PROGRESS
            </Box>
            <Box sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A', mt: 0.1 }}>
              3 / 5
            </Box>
          </Box>
          <Box sx={{
            width: 52, height: 52, borderRadius: '50%',
            background: `conic-gradient(#0D9488 0% 60%, #E2E8F0 60% 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 800, color: '#0F172A' }}>60%</Box>
          </Box>
        </Stack>

        <Stack spacing={0.85}>
          <ChecklistItem done text="Power down lobby AHU" />
          <ChecklistItem done text="Open access panel" />
          <ChecklistItem done text="Remove old filter" />
          <ChecklistItem active text="Inspect coil for debris" />
          <ChecklistItem text="Install MERV-13 + log model" />
        </Stack>
      </Box>
    </Box>
  )
}

function ChecklistItem({ text, done, active }: { text: string; done?: boolean; active?: boolean }) {
  return (
    <Box sx={{
      bgcolor: 'white', borderRadius: 1.5, p: 1,
      border: '1px solid', borderColor: active ? '#0D9488' : '#E2E8F0',
      boxShadow: active ? '0 0 0 3px rgba(13,148,136,0.12)' : 'none',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          width: 22, height: 22, borderRadius: '50%',
          bgcolor: done ? '#0D9488' : 'transparent',
          border: '2px solid', borderColor: done ? '#0D9488' : active ? '#0D9488' : '#CBD5E1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 11, fontWeight: 800,
        }}>{done ? '✓' : ''}</Box>
        <Box sx={{
          flex: 1, fontSize: 11.5,
          fontWeight: active ? 700 : 600,
          color: done ? '#94A3B8' : '#0F172A',
          textDecoration: done ? 'line-through' : 'none',
        }}>{text}</Box>
      </Stack>
    </Box>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// FULL APP SCREENS
// ════════════════════════════════════════════════════════════════════════════

// ─── Full — Dashboard ───────────────────────────────────────────────────────

function FullDashboardScreen() {
  return (
    <Box sx={{ px: 1.75, pb: 1.5, height: '100%', overflow: 'hidden',
      bgcolor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: 10.5, fontWeight: 700, color: '#94A3B8', letterSpacing: 0.5 }}>
            DASHBOARD · TODAY
          </Box>
          <Box sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A', mt: 0.2 }}>
            Hi, John
          </Box>
        </Box>
        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#7C3AED',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 13, fontWeight: 800 }}>JS</Box>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
        <DashTile big label="Open WOs" value="12" delta="+2" deltaColor="#DC2626" tone="#7C3AED" />
        <DashTile big label="Overdue" value="3" delta="-1" deltaColor="#10B981" tone="#DC2626" />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 1.75 }}>
        <DashTile label="Assets"   value="184" tone="#0EA5E9" />
        <DashTile label="Parts"    value="62"  tone="#F59E0B" />
        <DashTile label="Vendors"  value="14"  tone="#10B981" />
      </Stack>

      {/* Chart card */}
      <Box sx={{
        bgcolor: 'white', borderRadius: 2, p: 1.25,
        border: '1px solid #E2E8F0', mb: 1.25,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box sx={{ fontSize: 11, fontWeight: 800, color: '#0F172A' }}>
            Completed · 7 days
          </Box>
          <Box sx={{ fontSize: 9.5, fontWeight: 800, color: '#10B981',
            bgcolor: '#ECFDF5', px: 0.75, py: 0.25, borderRadius: 0.75 }}>+18%</Box>
        </Stack>
        <FakeBarChart />
      </Box>

      {/* Recent activity */}
      <Box sx={{ px: 0.25, fontSize: 11, fontWeight: 800, letterSpacing: 1,
        color: '#64748B', mb: 0.75 }}>RECENT</Box>
      <Box sx={{
        bgcolor: 'white', borderRadius: 1.5, p: 1.1,
        border: '1px solid #E2E8F0',
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
          <Box sx={{ flex: 1, fontSize: 11, color: '#0F172A' }}>
            <b>WO-00211</b> closed by Sarah
          </Box>
          <Box sx={{ fontSize: 10, color: '#94A3B8' }}>2m</Box>
        </Stack>
      </Box>
    </Box>
  )
}

function DashTile({ big, label, value, delta, deltaColor, tone }: {
  big?: boolean; label: string; value: string;
  delta?: string; deltaColor?: string; tone: string
}) {
  return (
    <Box sx={{
      flex: 1, bgcolor: 'white', borderRadius: 2,
      p: big ? 1.25 : 1, border: '1px solid #E2E8F0',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: big ? 0.4 : 0.2 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tone }} />
        {delta && (
          <Box sx={{ fontSize: 9, fontWeight: 800, color: deltaColor }}>{delta}</Box>
        )}
      </Stack>
      <Box sx={{ fontSize: big ? 19 : 15, fontWeight: 800, color: '#0F172A', lineHeight: 1 }}>{value}</Box>
      <Box sx={{ fontSize: big ? 9.5 : 9, color: '#64748B', mt: 0.4, fontWeight: 600 }}>{label}</Box>
    </Box>
  )
}

function FakeBarChart() {
  // Static CSS bars — pure visual, no data. Heights chosen to look
  // like a healthy weekly trend (small dip mid-week, recovery weekend).
  const bars = [38, 52, 46, 28, 62, 78, 70]
  const labels = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
  return (
    <Box>
      <Stack direction="row" spacing={0.6} alignItems="flex-end" sx={{ height: 64, mb: 0.5 }}>
        {bars.map((h, i) => (
          <Box key={i} sx={{
            flex: 1, height: `${h}%`,
            borderRadius: '4px 4px 2px 2px',
            background: `linear-gradient(180deg, #7C3AED, #A78BFA)`,
            opacity: i === bars.length - 1 ? 1 : 0.85,
          }} />
        ))}
      </Stack>
      <Stack direction="row" spacing={0.6}>
        {labels.map((l, i) => (
          <Box key={i} sx={{ flex: 1, textAlign: 'center', fontSize: 8.5, color: '#94A3B8' }}>{l}</Box>
        ))}
      </Stack>
    </Box>
  )
}

// ─── Full — Work order detail ───────────────────────────────────────────────

function FullWorkOrderScreen() {
  return (
    <Box sx={{ height: '100%', overflow: 'hidden',
      bgcolor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <AppBar number="WO-00102" title="Work order" />
      <Box sx={{ p: 1.75 }}>
        <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
          <StatusChip text="IN PROGRESS" bg="#FFEDD5" fg="#C2410C" />
          <StatusChip text="HIGH"        bg="#FEE2E2" fg="#DC2626" />
        </Stack>
        <Box sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A',
          lineHeight: 1.3, mb: 1 }}>
          Replace compressor — Chiller #2
        </Box>
        <Box sx={{ fontSize: 11, color: '#64748B', mb: 1.75, lineHeight: 1.55 }}>
          Coolant pressure dropping. Suspected bearing failure on the
          intake side — book downtime window with ops.
        </Box>

        {/* Info card */}
        <Box sx={{
          bgcolor: 'white', borderRadius: 2, p: 1.25,
          border: '1px solid #E2E8F0', mb: 1.25,
        }}>
          <InfoRow icon={<LocationOnOutlinedIcon sx={{ fontSize: 13 }} />}
            label="Site"  value="Bacsys HQ" />
          <Divider />
          <InfoRow icon={<AccessTimeRoundedIcon sx={{ fontSize: 13 }} />}
            label="Due"   value="Jun 8, 2026" />
          <Divider />
          <InfoRow icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
            label="Type"  value="Reactive" />
        </Box>

        {/* Assignee */}
        <Box sx={{
          bgcolor: 'white', borderRadius: 2, p: 1.25,
          border: '1px solid #E2E8F0', mb: 1.25,
        }}>
          <Box sx={{ fontSize: 9.5, fontWeight: 800, color: '#94A3B8',
            letterSpacing: 0.6, mb: 0.75 }}>
            ASSIGNED TO
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#7C3AED',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 10.5, fontWeight: 800 }}>JS</Box>
            <Box sx={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
              John Smith
            </Box>
          </Stack>
        </Box>

        {/* CTA */}
        <Box sx={{
          bgcolor: '#0D9488', color: 'white', borderRadius: 99,
          py: 1.25, textAlign: 'center', fontSize: 12, fontWeight: 800,
          boxShadow: '0 8px 18px -6px rgba(13, 148, 136, 0.55)',
          letterSpacing: 0.3,
        }}>
          ✓ MARK CLOSED
        </Box>
      </Box>
    </Box>
  )
}

// ─── Full — Asset detail ────────────────────────────────────────────────────

function FullAssetScreen() {
  return (
    <Box sx={{ height: '100%', overflow: 'hidden',
      bgcolor: '#F8FAFC', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <AppBar number="AST-0204" title="Asset" />
      <Box sx={{ p: 1.75 }}>
        {/* Hero image placeholder */}
        <Box sx={{
          height: 90, borderRadius: 2, mb: 1.25,
          background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: 32, opacity: 0.4,
          }}>❄</Box>
          <Box sx={{
            position: 'absolute', bottom: 8, left: 10, right: 10,
            color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
            textTransform: 'uppercase', opacity: 0.85,
          }}>HVAC · Chiller</Box>
        </Box>

        <Box sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A', mb: 0.4 }}>
          Chiller #2 — Roof
        </Box>
        <Box sx={{ fontSize: 10.5, color: '#64748B', mb: 1.5 }}>
          Carrier 30RB · S/N 2018-EM-44
        </Box>

        <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }}>
          <StatusChip text="OPERATIONAL" bg="#DCFCE7" fg="#15803D" />
          <StatusChip text="WARRANTY · 14M" bg="#EDE9FE" fg="#6D28D9" />
        </Stack>

        {/* Recent work orders for this asset */}
        <Box sx={{ px: 0.25, fontSize: 10, fontWeight: 800, letterSpacing: 1,
          color: '#64748B', mb: 0.75 }}>RECENT WORK ORDERS</Box>
        <Stack spacing={0.75}>
          <AssetWoRow code="WO-00102" title="Replace compressor" status="IN PROGRESS" statusColor="#C2410C" />
          <AssetWoRow code="WO-00089" title="Quarterly inspection" status="CLOSED" statusColor="#15803D" />
          <AssetWoRow code="WO-00074" title="Coolant top-up" status="CLOSED" statusColor="#15803D" />
        </Stack>
      </Box>
    </Box>
  )
}

function AssetWoRow({ code, title, status, statusColor }: {
  code: string; title: string; status: string; statusColor: string
}) {
  return (
    <Box sx={{
      bgcolor: 'white', borderRadius: 1.5, p: 1,
      border: '1px solid #E2E8F0',
    }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 4, height: 22, borderRadius: 99, bgcolor: statusColor }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: 8.5, fontWeight: 800, color: '#94A3B8',
            fontFamily: 'ui-monospace, monospace', mb: 0.15 }}>{code}</Box>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: '#0F172A',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Box>
        </Box>
        <Box sx={{ fontSize: 8, fontWeight: 800, color: statusColor,
          letterSpacing: 0.4 }}>{status}</Box>
      </Stack>
    </Box>
  )
}

// ─── Shared bits used across multiple mockup screens ───────────────────────

function AppBar({ number, title }: { number: string; title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{
      px: 1.5, py: 1.25, bgcolor: 'white', borderBottom: '1px solid #E2E8F0',
    }}>
      <ArrowBackIosNewRoundedIcon sx={{ fontSize: 14, color: '#0F172A' }} />
      <Box sx={{ flex: 1 }}>
        <Box sx={{ fontSize: 9, fontWeight: 800, color: '#94A3B8',
          fontFamily: 'ui-monospace, monospace', letterSpacing: 0.4 }}>{number}</Box>
        <Box sx={{ fontSize: 13, fontWeight: 800, color: '#0F172A', mt: 0.15 }}>{title}</Box>
      </Box>
      <Box sx={{ fontSize: 15, color: '#64748B' }}>⋯</Box>
    </Stack>
  )
}

function StatusChip({ text, bg, fg }: { text: string; bg: string; fg: string }) {
  return (
    <Box sx={{
      display: 'inline-block',
      px: 0.85, py: 0.3, borderRadius: 0.75,
      bgcolor: bg, color: fg,
      fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
    }}>{text}</Box>
  )
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.6 }}>
      <Box sx={{ color: '#94A3B8', display: 'flex' }}>{icon}</Box>
      <Box sx={{ fontSize: 10.5, color: '#64748B', flex: 1 }}>{label}</Box>
      <Box sx={{ fontSize: 11, color: '#0F172A', fontWeight: 700 }}>{value}</Box>
    </Stack>
  )
}

function Divider() {
  return <Box sx={{ height: 1, bgcolor: '#F1F5F9', my: 0.25 }} />
}

// ─── Install notes ──────────────────────────────────────────────────────────

function InstallNotes() {
  return (
    <Box sx={{
      p: { xs: 3, md: 3.5 }, borderRadius: 3,
      bgcolor: (t) => t.palette.mode === 'light' ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
      border: '1px solid', borderColor: 'divider',
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '0.1em', color: 'text.secondary', mb: 1.5 }}>
        Installing on Android
      </Typography>
      <Stack spacing={1.25}>
        <InstallStep number={1} title="Download the APK">
          Tap the green <strong>Download for Android</strong> button. Your
          browser will save the .apk file to your <em>Downloads</em> folder.
        </InstallStep>
        <InstallStep number={2} title="Allow installs from your browser">
          The first time, Android will ask permission for your browser (or
          file manager) to install apps. Toggle <strong>Allow from this
          source</strong> and continue.
        </InstallStep>
        <InstallStep number={3} title="Open the file & install">
          Tap the downloaded APK. Android will prompt to install — accept,
          wait a moment, then open the app and sign in with your CMMS
          credentials.
        </InstallStep>
      </Stack>
      <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 2.5, lineHeight: 1.55 }}>
        Hand the link to your team — same APK works for everyone in your org. iOS
        users will get the App Store link once we're through the review
        pipeline. We'll surface a banner here the day it goes live.
      </Typography>
    </Box>
  )
}

function InstallStep({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{
        flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
        bgcolor: 'primary.main', color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 800,
      }}>{number}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.55 }}>
          {children}
        </Typography>
      </Box>
    </Stack>
  )
}
